import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from 'sonner';
import { Eye, CheckCircle, XCircle, AlertCircle, Download, FileText } from 'lucide-react';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const InstructorApplications = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Action inputs
  const [adminNote, setAdminNote] = useState('');

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [applications, statusFilter, searchTerm]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/instructor-applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Başvurular yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = [...applications];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(app => 
        app.full_name?.toLowerCase().includes(term) ||
        app.email?.toLowerCase().includes(term)
      );
    }

    setFilteredApplications(filtered);
  };

  const handleApprove = async () => {
    if (!selectedApp) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/instructor-application/${selectedApp.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Başvuru onaylandı!');
      setShowDetailModal(false);
      fetchApplications();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error(error.response?.data?.detail || 'Onaylama sırasında hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApp) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/instructor-application/${selectedApp.id}/reject`,
        { reason: adminNote },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      toast.success('Başvuru reddedildi');
      setShowDetailModal(false);
      setAdminNote('');
      fetchApplications();
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Reddetme sırasında hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestCorrection = async () => {
    if (!selectedApp || !adminNote) {
      toast.error('Lütfen düzeltme notunu yazın');
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/instructor-application/${selectedApp.id}/request-correction`,
        { notes: adminNote },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      toast.success('Düzeltme istendi');
      setShowDetailModal(false);
      setAdminNote('');
      fetchApplications();
    } catch (error) {
      console.error('Request correction error:', error);
      toast.error('Düzeltme isteği gönderilirken hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Beklemede', className: 'bg-yellow-100 text-yellow-800' },
      approved: { label: 'Onaylandı', className: 'bg-green-100 text-green-800' },
      rejected: { label: 'Reddedildi', className: 'bg-red-100 text-red-800' },
      correction_requested: { label: 'Düzeltme Gerekli', className: 'bg-orange-100 text-orange-800' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Eğitmen Başvuruları</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <Input
                placeholder="İsim veya email ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Durumlar</SelectItem>
                  <SelectItem value="pending">Beklemede</SelectItem>
                  <SelectItem value="approved">Onaylandı</SelectItem>
                  <SelectItem value="rejected">Reddedildi</SelectItem>
                  <SelectItem value="correction_requested">Düzeltme Gerekli</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Applications Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Başvuru bulunamadı
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Başvuran</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Uzmanlık</TableHead>
                  <TableHead>Başvuru Tarihi</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredApplications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell className="font-medium">{app.full_name}</TableCell>
                    <TableCell>{app.email}</TableCell>
                    <TableCell className="capitalize">{app.expertise_area}</TableCell>
                    <TableCell>{formatDate(app.applied_at)}</TableCell>
                    <TableCell>{getStatusBadge(app.status)}</TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedApp(app);
                          setShowDetailModal(true);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Detay
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Başvuru Detayları</DialogTitle>
            <DialogDescription>
              {selectedApp?.full_name} - {selectedApp?.email}
            </DialogDescription>
          </DialogHeader>

          {selectedApp && (
            <div className="space-y-6">
              {/* Personal Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">Ad Soyad</Label>
                  <p className="mt-1">{selectedApp.full_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold">Email</Label>
                  <p className="mt-1">{selectedApp.email}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold">Telefon</Label>
                  <p className="mt-1">{selectedApp.phone}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold">Uzmanlık Alanı</Label>
                  <p className="mt-1 capitalize">{selectedApp.expertise_area}</p>
                </div>
              </div>

              {/* Bio */}
              {selectedApp.bio && (
                <div>
                  <Label className="text-sm font-semibold">Biyografi</Label>
                  <p className="mt-1 text-sm text-gray-600">{selectedApp.bio}</p>
                </div>
              )}

              {/* Financial Info */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <Label className="text-sm font-semibold">IBAN</Label>
                  <p className="mt-1 font-mono text-sm">{selectedApp.iban}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold">TC Kimlik No</Label>
                  <p className="mt-1 font-mono text-sm">{selectedApp.national_id}</p>
                </div>
              </div>

              {/* Documents */}
              <div className="pt-4 border-t">
                <Label className="text-sm font-semibold mb-2 block">Belgeler</Label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-purple-600 mr-2" />
                      <span className="text-sm">Sertifika/Belge</span>
                    </div>
                    {selectedApp.certificate_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`${API}${selectedApp.certificate_url}`, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Görüntüle
                      </Button>
                    )}
                  </div>

                  {selectedApp.id_document_url && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-purple-600 mr-2" />
                        <span className="text-sm">Kimlik Belgesi</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`${API}${selectedApp.id_document_url}`, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Görüntüle
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Status & Admin Notes */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-sm font-semibold">Mevcut Durum</Label>
                  {getStatusBadge(selectedApp.status)}
                </div>

                {selectedApp.admin_notes && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <Label className="text-sm font-semibold">Admin Notu</Label>
                    <p className="mt-1 text-sm">{selectedApp.admin_notes}</p>
                  </div>
                )}
              </div>

              {/* Actions - Only for pending or correction_requested */}
              {(selectedApp.status === 'pending' || selectedApp.status === 'correction_requested') && (
                <div className="pt-4 border-t space-y-4">
                  <Label className="text-sm font-semibold">İşlemler</Label>

                  {/* Admin Note Input */}
                  <div>
                    <Label className="text-sm">Not (Reddetme veya düzeltme için)</Label>
                    <Textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder="Reddediyorsanız sebep yazın veya düzeltme için gerekli bilgileri belirtin..."
                      rows={3}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      onClick={handleApprove}
                      disabled={actionLoading}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Onayla
                    </Button>

                    <Button
                      className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                      onClick={handleRequestCorrection}
                      disabled={actionLoading || !adminNote}
                    >
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Düzeltme İste
                    </Button>

                    <Button
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      onClick={handleReject}
                      disabled={actionLoading}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reddet
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InstructorApplications;
